import React, { memo, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import './ActivityFeed.scss'

// Icon definitions - defined outside component to avoid recreation
const ACTIVITY_ICONS = {
	created: (
		<svg viewBox="0 0 24 24" fill="currentColor">
			<path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
		</svg>
	),
	updated: (
		<svg viewBox="0 0 24 24" fill="currentColor">
			<path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
		</svg>
	),
	completed: (
		<svg viewBox="0 0 24 24" fill="currentColor">
			<path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
		</svg>
	),
	comment: (
		<svg viewBox="0 0 24 24" fill="currentColor">
			<path d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18zM18 14H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
		</svg>
	),
	uploaded: (
		<svg viewBox="0 0 24 24" fill="currentColor">
			<path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z" />
		</svg>
	),
	assigned: (
		<svg viewBox="0 0 24 24" fill="currentColor">
			<path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
		</svg>
	),
	deleted: (
		<svg viewBox="0 0 24 24" fill="currentColor">
			<path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
		</svg>
	),
	default: (
		<svg viewBox="0 0 24 24" fill="currentColor">
			<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
		</svg>
	)
}

// Color mapping - defined outside component
const ACTIVITY_COLORS = {
	created: 'success',
	updated: 'info',
	completed: 'success',
	comment: 'primary',
	uploaded: 'info',
	assigned: 'warning',
	deleted: 'danger'
}

/**
 * ActivityFeed - Timeline of recent activities
 * Memoized for performance optimization.
 *
 * @param {Array} activities - Array of activity objects
 * @param {string} title - Feed title
 * @param {number} maxItems - Maximum items to display
 * @param {boolean} showViewAll - Show "View All" link
 * @param {Function} onViewAll - View all callback
 * @param {boolean} loading - Show loading state
 */
const ActivityFeed = memo(({
	activities = [],
	title = 'Recent Activity',
	maxItems = 5,
	showViewAll = false,
	onViewAll,
	loading = false
}) => {
	// Memoize helper functions
	const getActivityIcon = useCallback((type) => {
		return ACTIVITY_ICONS[type] || ACTIVITY_ICONS.default
	}, [])

	const getActivityColor = useCallback((type) => {
		return ACTIVITY_COLORS[type] || 'primary'
	}, [])

	// Memoize displayed activities
	const displayedActivities = useMemo(() =>
		activities.slice(0, maxItems),
		[activities, maxItems]
	)

	if (loading) {
		return (
			<div className="activity-feed">
				<div className="activity-feed__header">
					<h3 className="activity-feed__title">{title}</h3>
				</div>
				<div className="activity-feed__list">
					{[...Array(3)].map((_, i) => (
						<div key={i} className="activity-feed__item activity-feed__item--skeleton">
							<div className="skeleton skeleton--icon"></div>
							<div className="skeleton skeleton--content">
								<div className="skeleton skeleton--text"></div>
								<div className="skeleton skeleton--time"></div>
							</div>
						</div>
					))}
				</div>
			</div>
		)
	}

	return (
		<div className="activity-feed">
			<div className="activity-feed__header">
				<h3 className="activity-feed__title">{title}</h3>
				{showViewAll && onViewAll && (
					<button className="activity-feed__view-all" onClick={onViewAll}>
						View All
					</button>
				)}
			</div>

			{displayedActivities.length === 0 ? (
				<div className="activity-feed__empty">
					<p>No recent activity</p>
				</div>
			) : (
				<div className="activity-feed__list">
					{displayedActivities.map((activity, index) => (
						<div
							key={activity.id || index}
							className={`activity-feed__item activity-feed__item--${getActivityColor(activity.type)}`}
						>
							<div className="activity-feed__icon">{getActivityIcon(activity.type)}</div>
							<div className="activity-feed__content">
								<p className="activity-feed__text">
									{activity.user && (
										<span className="activity-feed__user">{activity.user}</span>
									)}
									{' '}
									{activity.action || activity.message}
									{activity.target && (
										<span className="activity-feed__target"> {activity.target}</span>
									)}
								</p>
								<span className="activity-feed__time">{activity.timestamp || activity.time}</span>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	)
})

ActivityFeed.displayName = 'ActivityFeed'

ActivityFeed.propTypes = {
	activities: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			type: PropTypes.string,
			user: PropTypes.string,
			action: PropTypes.string,
			message: PropTypes.string,
			target: PropTypes.string,
			timestamp: PropTypes.string,
			time: PropTypes.string
		})
	),
	title: PropTypes.string,
	maxItems: PropTypes.number,
	showViewAll: PropTypes.bool,
	onViewAll: PropTypes.func,
	loading: PropTypes.bool
}

export default ActivityFeed
